// From: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/MouseFieldPlaceholder/index.tsx

import { Box } from '@modusign/design-system';
import { useSigners } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { useRef } from 'react';
import { useEvent, useKeyPressEvent } from 'react-use';

import {
  toAbsoluteNumber,
  toAbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getDefaultSignatureSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getDefaultSignatureSize';
import { Viewport } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import AddressFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/AddressFieldMousePlaceholder';
import CompanyNameFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/CompanyNameFieldMousePlaceholder';
import DateFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/DateFieldMousePlaceholder';
import DropdownFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/DropdownFieldMousePlaceholder';
import NameFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/NameFieldMousePlaceholder';
import SigningDateFieldMousePlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/MouseFieldPlaceholder/SigningDateFieldMousePlaceholder';
import { isSignerResizableSignatureField } from '@app/features/apply-react-signing/libs/isSignerResizableSignatureField';
import useMouseClientPosition from '@app/hooks/useMouseClientPosition';

import CheckboxGroupMousePlaceholder from './CheckboxGroupMousePlaceholder';
import SignatureMousePlaceholder from './SignatureMousePlaceholder';
import TextMousePlaceholder from './TextMousePlaceholder';

interface SignerMouseFieldPlaceholderProps {
  viewports: Viewport[];
  scale: number;
}

function SignerMouseFieldPlaceholder({ viewports, scale }: SignerMouseFieldPlaceholderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { clientX, clientY } = useMouseClientPosition(ref);

  let mousePlaceholderElement: React.ReactNode | null;

  const { ui, resetInsertingField } = useUIState();

  useKeyPressEvent('Escape', () => {
    if (ui.insertingField && mousePlaceholderElement) {
      resetInsertingField();
    }
  });
  useEvent('contextmenu', (event: MouseEvent) => {
    if (ui.insertingField && mousePlaceholderElement) {
      // 브라우저에서 마우스 우클릭하면 나오는 컨텍스트 메뉴가 안나오게 합니다.
      event.preventDefault();
      resetInsertingField();
    }
  });

  const { signers } = useSigners();

  const signer = signers.find((p) => p.id === ui.insertingField?.participantId);

  if (!ui.insertingField || ui.insertingField.participantId !== signer?.id) {
    return null;
  }

  // TODO(@DevSDK): Maybe count
  const participantOrder = signer.index + 1;
  const field = ui.insertingField?.field;

  if (field?.type === 'CHECKBOX_GROUP') {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const viewport = viewports.find((viewport) => viewport.pageNumber === field.pages[0])!;

    mousePlaceholderElement = (
      <CheckboxGroupMousePlaceholder
        // TODO(@DevSDK): Find better way to provide latestFieldData
        participantOrder={participantOrder}
        showStar={field.config.required}
        size={toAbsoluteNumber(field.size.width, createAbsoluteSize(viewport))}
      />
    );
  } else if (field?.type === 'SIGNATURE') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

    const size = isSignerResizableSignatureField(field)
      ? getDefaultSignatureSize(field.config.allow, scale)
      : toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <SignatureMousePlaceholder
        allow={field.config.allow}
        participantOrder={participantOrder}
        size={size}
        uploadOnly={field.config.disableSignatureGenerator}
      />
    );
  } else if (field?.type === 'TEXT') {
    const { page } = field.position;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!;
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <TextMousePlaceholder
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        format={field.config.format}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'DROPDOWN') {
    mousePlaceholderElement = (
      <DropdownFieldMousePlaceholder
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'NAME') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <NameFieldMousePlaceholder
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'COMPANY_NAME') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <CompanyNameFieldMousePlaceholder
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'ADDRESS') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <AddressFieldMousePlaceholder
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'DATE') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <DateFieldMousePlaceholder
        dateFormat={field.config.displayFormat}
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else if (field?.type === 'SIGNING_DATE') {
    const { page } = field.position;
    const viewport = viewports.find((viewport) => viewport.pageNumber === page)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const size = toAbsoluteSize(field.size, viewport);

    mousePlaceholderElement = (
      <SigningDateFieldMousePlaceholder
        dateFormat={field.config.displayFormat}
        fontFamily={field.config.fontStyle.type}
        fontSize={field.config.fontStyle.size}
        participantOrder={participantOrder}
        required={field.config.required}
        scale={scale}
        size={size}
        textAlign={field.config.textAlign}
      />
    );
  } else {
    mousePlaceholderElement = null;
  }

  return (
    <Box
      ref={ref}
      /**
       * @see https://modusign.slack.com/archives/CBT88M56C/p1671091123702209
       */
      style={{
        transform: `translate(${clientX}px, ${clientY}px)`,
      }}
      sx={{
        cursor: 'default',
        /**
         * useMount로 마우스 포인터 위치를 가져올 때
         * mousemove 이벤트가 발생하지 않으면 좌표가 (0,0) 값이라
         * 마우스를 움직이기 전까지 화면 왼쪽 위 구석에 나타나는 문제가 발생합니다.
         * 마우스 커서 좌표를 추적하기 위해 컴포넌트 마운트 상태, 렌더링을 유지하고
         * css display 속성으로 숨김처리하게 구현했습니다.
         */
        display: !ui.insertingField || !mousePlaceholderElement ? 'none' : undefined,
        left: 0,
        position: 'fixed',
        top: 0,
        zIndex: 501, // $mouse-marker-z-index
        pointerEvents: 'none',
      }}
    >
      {mousePlaceholderElement}
    </Box>
  );
}

export default SignerMouseFieldPlaceholder;
